"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const teamMembers = [{
  name: "Dr. Aris Thorne",
  role: "PhD Food Science",
  bio: "Chief Formulator with 15 years in nutraceutical science. Dr. Thorne specializes in stabilizing complex probiotic and adaptogen profiles for counter-pressure carbonation, ensuring potency and flavor integrity in every can.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/aris_thorne.png"
}, {
  name: "Marcus Vane",
  role: "MS Chemical Engineering",
  bio: "Our Operations Director and a specialist in counter-pressure canning systems. Marcus oversees the technical precision of our production lines, scaling emerging wellness brands from pilot batches to full-market distribution.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/marcus_vane.webp"
}, {
  name: "Elena Rodriguez",
  role: "PCQI Certified",
  bio: "As our Quality Assurance Lead, Elena ensures our FDA-registered facility exceeds all safety protocols. Her rigorous compliance standards guarantee that every functional beverage meets the highest industry certifications.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/elena_rodriguez.webp"
}, {
  name: "Jordan Lee",
  role: "Flavor Technologist",
  bio: "Leading our R&D efforts, Jordan bridges the gap between clinical health benefits and premium sensory experiences. They focus on mask-flavoring functional ingredients to create refreshing, market-ready beverage profiles.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/jordan_lee.webp"
}];
export default function TheSilkBowlTeam() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="39349" id="our-expert-team" ref={ref} className="bg-background text-foreground py-20 px-4 md:px-8 lg:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-sans font-semibold mb-4 tracking-tighter text-foreground">
            The Formulation Experts
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Our team combines scientific rigor with beverage innovation to bring your functional drinks to life with technical precision.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => <TeamMemberCard key={member.name} member={member} index={index} isVisible={isVisible} />)}
        </div>
      </div>
    </section>;
}
function TeamMemberCard({
  member,
  index,
  isVisible
}: {
  member: (typeof teamMembers)[0];
  index: number;
  isVisible: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div data-index={index} className={`group transition-all duration-700 ease-out delay-[${index * 150}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <Card className="bg-card border-border/40 overflow-hidden h-full flex flex-col hover:shadow-xl transition-shadow duration-300 rounded-lg">
        {}
        <div className="relative aspect-[3/4] overflow-hidden bg-muted">
          {member.image ? <img src={member.image} alt={member.name} className="w-full h-full object-cover transition-all duration-500 filter grayscale group-hover:grayscale-0 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/20 text-secondary">
              <User className="w-20 h-20 opacity-50" />
            </div>}
        </div>

        {}
        <CardHeader className="text-center pb-2 pt-6">
          <CardTitle className="text-xl font-sans font-semibold tracking-tight">
            {member.name}
          </CardTitle>
          <CardDescription className="text-primary font-mono uppercase tracking-widest text-[10px] mt-1 bg-primary/5 inline-block mx-auto px-2 py-0.5 rounded-sm">
            {member.role}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-grow flex flex-col items-center pb-6">
          <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full text-center">
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              <div className="pt-2 pb-4 text-sm text-muted-foreground leading-relaxed px-2">
                {member.bio}
              </div>
            </CollapsibleContent>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="mt-2 text-xs font-medium text-muted-foreground hover:text-primary group/btn w-full sm:w-auto transition-colors">
                {isOpen ? "Hide Profile" : "View Profile"}
                {isOpen ? <ChevronUp className="ml-1 h-3 w-3 transition-transform duration-200" /> : <ChevronDown className="ml-1 h-3 w-3 transition-transform duration-200 group-hover/btn:translate-y-0.5" />}
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
        </CardContent>
      </Card>
    </div>;
}